import SuboptimalState from '@components/SuboptimalState/SuboptimalState';
import {
    useUpdateGlobalPreference,
    useUpdateSubscriptionPreferenceForChannel,
    useUserSubscriptionPreferencesForChannel,
} from '@hooks/subscriptionGroups/useSubscriptionPreference';
import useNotify from '@hooks/toaster/useNotify';
import { useLocale } from '@hooks/useLocale';
import { useTracking } from '@hooks/useTracking';
import {
    decodeSubToken,
    UnsubUrlQueryParam,
    type CommunicationChannel,
    type SubscriptionGroupWithUserPreference,
    type UserGlobalPreferenceUpdateRequest,
    type UserPreferenceUpdateRequest,
} from '@lightdash/common';
import { SubscriptionGroupType } from '@lightdash/common/src/types/subscriptionGroup';
import { type CategoriesWithGroupsAndUserPreference } from '@lightdash/common/src/types/userSubscriptionPreference';
import { Box, Button, Flex, Loader, Stack, Text } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { EventName } from 'types/Events';
import { QueryKeys } from 'types/UseQuery';
import { getChannel } from '../utils';
import Node from './Node';
import TestUserSubscriptionPreferences from './TestUserSubscriptionPreferences';

interface NodeData {
    id: string;
    name: string;
    categoryId: string | null;
    description: string;
    type: string | undefined;
    weight: number | undefined;
    children: NodeData[] | undefined;
    userPreference: boolean;
    isGlobal?: boolean;
}

interface CategoryWithGroups {
    category: {
        id: string;
        isGlobal: boolean;
    };
    groups: { id: string; userPreference: boolean; campaignId: string }[];
}

const UserSubscriptionPreferences = () => {
    const [treeData, setTreeData] = useState<NodeData[]>([]);
    const [isGlobalPreference, setIsGlobalPreference] = useState<boolean>(true);
    const [isGlobalClicked, setIsGlobalClicked] = useState(false);
    const [changedItemIds, setChangedItemIds] = useState<Set<string>>(
        new Set(),
    );
    const location = useLocation();
    const { t } = useLocale();
    const { showToastSuccess, showToastError } = useNotify();
    const { track } = useTracking();

    const searchParams = new URLSearchParams(location.search);

    const subToken = searchParams.get('subToken') || '';
    const parsedSubToken = decodeSubToken(subToken);

    const campaign =
        (parsedSubToken?.campaignId ?? searchParams.get('campaign')) || '';
    const channel =
        (parsedSubToken?.channel ?? searchParams.get('channel')) || '';

    const userId = (parsedSubToken?.userId ?? searchParams.get('userId')) || '';
    const projectId =
        (parsedSubToken?.projectId ?? searchParams.get('projectId')) || '';

    const channelIdentifier =
        parsedSubToken?.channelIdentifier ??
        searchParams.get('channelIdentifier');

    const mode = searchParams.get('mode') || '';
    const queryClient = useQueryClient();

    const {
        data: userSubscriptionPreference,
        isFetching: isSubscriptionPreferenceLoading,
        isError: isSubscriptionPreferenceError,
    } = useUserSubscriptionPreferencesForChannel({
        channel: getChannel(channel),
        userId: userId,
        projectId: projectId,
        channelIdentifier: channelIdentifier || '',
    });

    const {
        mutateAsync: updateSubscriptionPreference,
        isLoading: isUpdatingPreference,
    } = useUpdateSubscriptionPreferenceForChannel();

    const {
        mutateAsync: mutateGlobalPreference,
        isLoading: isAllUnsubscribed,
    } = useUpdateGlobalPreference();

    const getCategoryPreferenceValue = useCallback(
        (groups: NodeData[]): boolean => {
            return groups.every((group) => group.userPreference === true);
        },
        [],
    );

    const transformGroup = useCallback(
        (
            group: SubscriptionGroupWithUserPreference,
            globalPreference: boolean,
        ) => ({
            id: group.id,
            categoryId: group.categoryId,
            name: group.name,
            description: group.description,
            type: group.type,
            weight: group.weight,
            userPreference:
                group.type !== SubscriptionGroupType.REQUIRED &&
                !globalPreference
                    ? globalPreference
                    : group.userPreference,
            children: undefined,
            isGlobal: group.isGlobal ?? false,
        }),
        [],
    );

    const transformData = useCallback(
        (
            data: CategoriesWithGroupsAndUserPreference,
            globalPreference: boolean,
        ) => {
            const newData: NodeData[] = [];
            data.forEach((item) => {
                const { category, groups } = item;

                if (category.isGlobal) {
                    groups.forEach((group) => {
                        newData.push(transformGroup(group, globalPreference));
                    });
                } else {
                    const sortedGroups = groups
                        .map((group) => transformGroup(group, globalPreference))
                        .sort((a, b) => {
                            if (!a.weight || !b.weight) {
                                return 0;
                            }
                            return a.weight - b.weight;
                        });

                    newData.push({
                        id: category.id,
                        categoryId: null,
                        name: category.name,
                        description: category.description,
                        weight: category.weight,
                        userPreference:
                            getCategoryPreferenceValue(sortedGroups),
                        type: undefined,
                        children: sortedGroups,
                    });
                }
            });
            newData.sort((a, b) => {
                if (!a.weight || !b.weight) {
                    return 0;
                }
                return a.weight - b.weight;
            });
            return [...newData];
        },
        [getCategoryPreferenceValue, transformGroup],
    );

    useEffect(() => {
        if (userSubscriptionPreference) {
            setIsGlobalPreference(userSubscriptionPreference.globalPreference);
            setTreeData(
                transformData(
                    userSubscriptionPreference.categoryWisePreference,
                    userSubscriptionPreference.globalPreference,
                ),
            );
            // Clear changed items when fresh data is loaded
            setChangedItemIds(new Set());
        }
    }, [getCategoryPreferenceValue, transformData, userSubscriptionPreference]);

    const toggleUserPreference = useCallback(
        (id: string) => {
            const updatedChangedIds = new Set(changedItemIds);

            const updatedTreeData = treeData.map((item) => {
                if (item.id === id) {
                    item.userPreference = !item.userPreference;
                    // Track the category/group that was directly clicked
                    updatedChangedIds.add(id);

                    if (item.children) {
                        item.children = item.children.map((child) => {
                            if (child.type !== SubscriptionGroupType.REQUIRED) {
                                // Track children that are affected by parent toggle
                                updatedChangedIds.add(child.id);
                                return {
                                    ...child,
                                    userPreference: item.userPreference,
                                };
                            }
                            return {
                                ...child,
                                userPreference: child.userPreference,
                            };
                        });
                    }
                } else if (item.children) {
                    item.children = item.children.map((child) => {
                        if (
                            child.id === id &&
                            item.type !== SubscriptionGroupType.REQUIRED
                        ) {
                            child.userPreference = !child.userPreference;
                            // Track the child that was directly clicked
                            updatedChangedIds.add(child.id);
                        }
                        return child;
                    });
                }

                const allChildrenTrue =
                    item.children &&
                    item.children.every((child) => child.userPreference);
                const anyChildFalse =
                    item.children &&
                    item.children.some((child) => !child.userPreference);

                // If parent state changes due to children updates, track the parent
                const previousUserPreference = item.userPreference;
                if (allChildrenTrue) {
                    item.userPreference = true;
                } else if (anyChildFalse) {
                    item.userPreference = false;
                }

                if (previousUserPreference !== item.userPreference) {
                    updatedChangedIds.add(item.id);
                }

                return item;
            });

            setTreeData(updatedTreeData);
            setChangedItemIds(updatedChangedIds);
        },
        [treeData, changedItemIds],
    );

    const processChangedItemsOnly = useCallback(
        (
            subscriptionChannel: CommunicationChannel,
        ): UserPreferenceUpdateRequest => {
            let globalCategory: CategoryWithGroups = {
                category: {
                    id: '',
                    isGlobal: true,
                },
                groups: [],
            };

            let categories: CategoryWithGroups[] = [];
            const processedCategoryIds = new Set<string>();

            treeData.forEach((item) => {
                if (!item.children || item.children.length === 0) {
                    // This is a global group
                    if (changedItemIds.has(item.id)) {
                        globalCategory.groups.push({
                            id: item.id,
                            userPreference: item.userPreference,
                            campaignId: campaign,
                        });
                    }
                } else {
                    // This is a category with children
                    const changedChildren = item.children.filter((child) =>
                        changedItemIds.has(child.id),
                    );

                    // If any children changed or the category itself changed
                    if (
                        changedChildren.length > 0 ||
                        changedItemIds.has(item.id)
                    ) {
                        if (!processedCategoryIds.has(item.id)) {
                            let category: CategoryWithGroups = {
                                category: {
                                    id: item.id,
                                    isGlobal: false,
                                },
                                groups: changedChildren.map(
                                    (child: NodeData) => ({
                                        id: child.id,
                                        userPreference: child.userPreference,
                                        campaignId: campaign,
                                    }),
                                ),
                            };
                            categories.push(category);
                            processedCategoryIds.add(item.id);
                        }
                    }
                }
            });

            return {
                userId,
                channel: subscriptionChannel,
                data: [globalCategory, ...categories].filter(
                    (category) => category.groups.length > 0,
                ),
                channelId: channelIdentifier || '',
            };
        },
        [treeData, userId, channelIdentifier, changedItemIds, campaign],
    );

    const savePreference = useCallback(async () => {
        track(
            {
                name: EventName.SAVE_PREFERENCE_CLICKED,
                payload: processChangedItemsOnly(getChannel(channel)),
            },
            projectId,
        );
        // Only update if there are actual changes
        if (changedItemIds.size > 0) {
            await updateSubscriptionPreference({
                payload: processChangedItemsOnly(getChannel(channel)),
            });
            showToastSuccess({
                title: t(
                    'subscription_preference.your_preferences_have_been_saved',
                ),
            });
            let payload: UserGlobalPreferenceUpdateRequest = {
                userId: userId,
                channel: getChannel(channel),
                projectId: projectId,
                actionTaken: true,
                campaignId: campaign,
            };
            if (channelIdentifier) {
                payload = {
                    ...payload,
                    channelId: channelIdentifier,
                };
            }
            await mutateGlobalPreference({
                payload,
            });
            // Clear changed items after successful save
            setChangedItemIds(new Set());
        } else {
            // No changes to save - could show an info message or do nothing
            // For now, we'll just do nothing as there are no changes
        }
    }, [
        track,
        processChangedItemsOnly,
        channel,
        changedItemIds.size,
        updateSubscriptionPreference,
        showToastSuccess,
        t,
        userId,
        projectId,
        campaign,
        channelIdentifier,
        mutateGlobalPreference,
    ]);

    const updateGlobalPreference = useCallback(async () => {
        setIsGlobalClicked(true);
        let payload: UserGlobalPreferenceUpdateRequest = {
            userId: userId,
            channel: getChannel(channel),
            projectId: projectId,
            actionTaken: false,
            campaignId: campaign,
        };
        if (channelIdentifier) {
            payload = {
                ...payload,
                channelId: channelIdentifier,
            };
        }
        track(
            {
                name: EventName.GLOBAL_PREFERENCE_UPDATE_CLICKED,
                payload,
            },
            projectId,
        );
        await mutateGlobalPreference({
            payload,
        });
        setIsGlobalPreference(false);
        setIsGlobalClicked(false);
        await queryClient.invalidateQueries([
            QueryKeys.GET_SUBSCRIPTION_PREFERENCE_FOR_CHANNEL,
        ]);
    }, [
        userId,
        channel,
        projectId,
        campaign,
        channelIdentifier,
        track,
        mutateGlobalPreference,
        queryClient,
    ]);

    useEffect(() => {
        const isValidUnsubUrl = projectId && userId && channel;
        if (!isValidUnsubUrl) {
            showToastError({
                title: t(
                    'subscription_preference.user_preferences_url_invalid_error',
                ),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isSubscriptionPreferenceLoading) {
        return (
            <Box className="flex justify-center items-center w-screen h-screen">
                <Flex className="p-3.5" align="center">
                    <Loader size={11} />
                    <Text className="text-sm font-normal text-blu-800 px-1.5">
                        {t('subscription_body.loading')}
                    </Text>
                </Flex>
            </Box>
        );
    }

    //Info: If the subscription preference API errors, show the invalid link error
    if (!isSubscriptionPreferenceLoading && isSubscriptionPreferenceError) {
        return (
            <Box className="flex flex-col justify-center items-center px-4 w-screen h-screen">
                <SuboptimalState title={t('common.invalid_urllink_error')} />
            </Box>
        );
    }

    if (userSubscriptionPreference && !isGlobalPreference)
        return (
            <Box className="flex flex-col justify-center items-center p-4 w-screen h-screen">
                <Box className="w-full lg:w-2/5">
                    <Text className="text-lg font-medium text-gray-800">
                        {t(
                            'subscription_preference.you_ve_been_unsubscribed_from',
                            {
                                channel: channel,
                            },
                        )}
                    </Text>
                    <Text className="mt-3 text-sm font-medium text-gray-600">
                        {t('subscription_preference.unsubscribe_all')}
                    </Text>
                    <Text className="flex mt-3.5 text-gray-600 font-medium text-xs">
                        {t(
                            'unsubscribe_group.manage_all_your_subscription_preferences',
                        )}{' '}
                        <Text
                            className="ml-1 underline cursor-pointer"
                            onClick={() => setIsGlobalPreference(true)}
                        >
                            {t('unsubscribe_group.here')}
                        </Text>
                    </Text>
                </Box>
            </Box>
        );

    return (
        <Box className="flex flex-col justify-center items-center p-4 w-screen h-screen">
            <Stack className="flex flex-col justify-center w-full h-full md:w-2/5">
                <Flex className="flex flex-col flex-shrink-0 gap-1">
                    {mode === UnsubUrlQueryParam.TEST && (
                        <TestUserSubscriptionPreferences />
                    )}
                    <Text className="text-lg font-medium text-gray-800">
                        {t('subscription_preference.title', {
                            channel: channel,
                        })}
                    </Text>
                    <Text className="text-sm font-medium text-gray-600">
                        {t(
                            'subscription_preference.well_only_send_you_content_that_you_re_interested_in',
                        )}
                    </Text>
                </Flex>
                <Box className="flex-1 overflow-y-auto min-h-0 lg:flex-none lg:max-h-[60vh]">
                    {treeData.map((item) => {
                        //Do not show Global groups
                        if (item.isGlobal) return;

                        return (
                            <Node
                                key={item.id}
                                nodeData={item}
                                onChecked={(id) => toggleUserPreference(id)}
                            />
                        );
                    })}
                </Box>
                <Text className="mt-3 text-xs text-gray-600">
                    {t('subscription_preference.unsubscribe_all')}
                </Text>
                <Flex className="flex-col flex-shrink-0 gap-2 md:flex-row">
                    <Button
                        className="bg-black rounded py-1.5 px-3 text-white text-sm font-medium hover:!bg-black"
                        onClick={() => savePreference()}
                        loading={isUpdatingPreference}
                    >
                        {isUpdatingPreference
                            ? t('subscription_preference.saving_preferences')
                            : t('subscription_preference.save_preferences')}
                    </Button>
                    <Button
                        className="bg-gray-500 rounded py-1.5 px-3 text-black text-sm font-medium hover:!bg-gray-500"
                        onClick={() => updateGlobalPreference()}
                        loading={isAllUnsubscribed && isGlobalClicked}
                    >
                        {t('subscription_preference.unsubscribe_from_all')}
                    </Button>
                </Flex>
            </Stack>
        </Box>
    );
};

export default UserSubscriptionPreferences;
